import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ChapterBookAuthorListControl } from '@/module/Author/Chapter/components/ChapterBookAuthorListControl';
import { TextControl } from '@/module/Author/Text/TextControl';
import { BookMembershipSelectControl } from '@/module/Nomen/BookMembership/BookMembershipSelectControl';
import { ChapterMembershipSelectControl } from '@/module/Nomen/ChapterMembership/ChapterMembershipSelectControl';
import { ChapterTypeSelectControl } from '@/module/Nomen/ChapterType/ChapterTypeSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, TextInputControl } from '@/core/components';

import { ChapterSchema } from './model/ChapterSchema';

interface ChapterFormProps extends FormSubmit<ChapterSchema> {
  title: string;
  showWosFields: boolean;
  showScopusFields: boolean;
}

export function ChapterForm(props: ChapterFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit, showScopusFields, showWosFields } =
    props;
  const { t } = useTranslation();
  const form = useForm<ChapterSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<ChapterSchema>
              name="bookName"
              label={t('chapters.form.fields.bookName')}
              isSubmitting={isSubmitting}
            />
            {showWosFields && (
              <TextInputControl<ChapterSchema>
                name="bookSeries"
                label={t('chapters.form.fields.bookSeries')}
                isSubmitting={isSubmitting}
              />
            )}
            {showWosFields && (
              <ChapterTypeSelectControl<ChapterSchema>
                label={t('chapters.form.fields.chapterType')}
                name="type"
                isSubmitting={isSubmitting}
              />
            )}
            <TextControl
              isSubmitting={isSubmitting}
              showWosFields={showWosFields}
              showScopusFields={showScopusFields}
            />
            <Group>
              <TextInputControl<ChapterSchema>
                name="bookIsbn.printCode"
                label={t('chapters.form.fields.isbnPrint')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl<ChapterSchema>
                name="bookIsbn.onlineCode"
                label={t('chapters.form.fields.isbnOnline')}
                isSubmitting={isSubmitting}
              />
            </Group>
          </Content>
          <Content type="full">
            <ChapterBookAuthorListControl<ChapterSchema>
              name="bookAuthors"
              isSubmitting={isSubmitting}
              label={t('chapters.form.fields.bookAuthors')}
              renderMembership={({ membershipName }) => (
                <BookMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<ChapterSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <ChapterMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
